import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import type { Printer, PrintAgent, PrinterQuery, PrintJob } from 'api/types';
import PrinterFormModal from 'components/modals/modules/print-module/PrinterFormModal';
import PrintTestPageModal, { queueTestPage } from 'components/modals/modules/print-module/PrintTestPageModal';
import { HealthChip, OnlineDot, fmtTime, apiMessage } from './printUi';

/**
 * Printers (KOT printing, architecture section 17): the receipt printers the
 * Print Agents reach, each on one agent. Add, edit, test and delete. Phase 1 is
 * network printers in text mode. A department picks its printer on the
 * Departments screen; a printer still used there cannot be deleted.
 *
 * Paged, searched and filtered on the server.
 */

interface Filters { agentId?: string; includeInactive?: string }

const PrintersScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [draftFilters, setDraftFilters] = useState<Filters>({});
  const [query, setQuery] = useState<PrinterQuery>({ page: 1, pageSize: 25 });
  const [rows, setRows] = useState<Printer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [agents, setAgents] = useState<PrintAgent[]>([]);

  const [form, setForm] = useState<{ mode: 'create' | 'edit'; record: Printer | null } | null>(null);
  const [deleting, setDeleting] = useState<Printer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // The test page is sent by the click (one page, even under StrictMode); the dialog follows it.
  const [testing, setTesting] = useState<{ printer: Printer; queued: Promise<PrintJob> } | null>(null);

  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = allowedActions.includes('delete');
  const canTest = canEdit || allowedActions.includes('manage');

  // Agents for the form and the filter: every agent not revoked (a printer can wait on one pending approval).
  const loadAgents = useCallback(async () => {
    try {
      const r = await printService.getAgents({ page: 1, pageSize: 100 });
      setAgents(r.data.filter((a) => a.agentStatus !== 'REVOKED'));
    } catch { setAgents([]); }
  }, []);
  useEffect(() => { void loadAgents(); }, [loadAgents]);

  useEffect(() => {
    const t = setTimeout(() => setQuery((q) => ({ ...q, search: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setQuery((q) => ({ ...q, page: 1, agentId: filters.agentId || undefined, includeInactive: filters.includeInactive === 'true' || undefined }));
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await printService.getPrinters(query);
      setRows(r.data);
      setTotal(r.pagination.totalItems);
    } catch (err) {
      setLoadError(apiMessage(err, 'The printers could not be loaded.'));
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { void load(); }, [load]);

  const handleSaved = useCallback(() => { void load(); void loadAgents(); }, [load, loadAgents]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const headers = ['Code', 'Printer', 'Agent', 'Agent online', 'Address', 'Paper', 'Character set', 'Health', 'Health detail', 'Open jobs', 'Status'];
    const lines = rows.map((r) => [r.printerCode, r.printerName, r.agentName ?? '', r.agentOnline ? 'Yes' : 'No', `${r.host ?? ''}:${r.port}`,
      `${r.paperWidthMm} mm`, r.codePage, r.healthState, r.healthDetail ?? '', r.openJobCount, r.isActive ? 'Active' : 'Inactive']
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const url = URL.createObjectURL(new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `printers-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const handleDelete = useCallback(async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await printService.deletePrinter(deleting.id, deleting.rowVersion);
      notificationService.toast.success(`"${deleting.printerName}" deleted.`);
      setDeleting(null);
      void load();
    } catch (err) {
      // 409: still used by departments — the API names them. Keep the dialog's message, show the API's words.
      const msg = apiMessage(err, 'The printer could not be deleted.');
      setDeleting(null);
      setDeleteError(msg);
      notificationService.toast.error(msg, 8000);
    } finally { setIsDeleting(false); }
  }, [deleting, load]);

  const columns = useMemo((): SyutGridColumn<Printer>[] => [
    { id: 'printerCode', header: 'Code', accessorKey: 'printerCode', enableSorting: true, size: 100 },
    { id: 'printerName', header: 'Printer', accessorKey: 'printerName', enableSorting: true, size: 180 },
    { id: 'agentName', header: 'Agent', accessorKey: 'agentName', enableSorting: true, size: 190,
      cell: ({ row }) => (
        <span data-agent-cell>
          <OnlineDot online={row.original.agentOnline} title={row.original.agentOnline ? 'Agent online' : 'Agent offline'} />
          {row.original.agentName ?? '—'}
          {row.original.agentStatus && row.original.agentStatus !== 'ACTIVE' && (
            <span className="prn-sub">{row.original.agentStatus === 'PENDING_APPROVAL' ? 'waiting for approval' : row.original.agentStatus.toLowerCase()}</span>
          )}
        </span>
      ) },
    { id: 'address', header: 'Address', accessorKey: 'host', enableSorting: false, size: 160,
      cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.host ?? '—'}:{row.original.port}</span> },
    { id: 'paperWidthMm', header: 'Paper', accessorKey: 'paperWidthMm', enableSorting: true, size: 90, cell: ({ row }) => `${row.original.paperWidthMm} mm` },
    { id: 'healthState', header: 'Health', accessorKey: 'healthState', enableSorting: true, size: 200,
      cell: ({ row }) => (
        <span>
          <HealthChip state={row.original.healthState} detail={row.original.healthDetail} />
          {(row.original.healthDetail || row.original.healthAt) && (
            <span className="prn-sub">{row.original.healthDetail ?? ''}{row.original.healthAt ? `${row.original.healthDetail ? ' · ' : ''}${fmtTime(row.original.healthAt)}` : ''}</span>
          )}
        </span>
      ) },
    { id: 'openJobCount', header: 'Open jobs', accessorKey: 'openJobCount', enableSorting: true, size: 100,
      cell: ({ row }) => (row.original.openJobCount ? <strong>{row.original.openJobCount}</strong> : <span style={{ color: 'var(--text-muted)' }}>0</span>) },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
      cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
  ], []);

  const getRowActions = useCallback((row: Printer): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (canTest && row.isActive) items.push({ id: 'test', label: 'Print test page', icon: <IconSystem.print size={16} />, onClick: () => setTesting({ printer: row, queued: queueTestPage(row) }),
      description: 'Queues a test page and shows whether it printed.' });
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setForm({ mode: 'edit', record: row }) });
    if (canDelete && row.isActive) items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => { setDeleteError(null); setDeleting(row); }, variant: 'danger' });
    return items;
  }, [canTest, canEdit, canDelete]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setForm({ mode: 'create', record: null }), tooltip: 'Add Printer', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => { void load(); void loadAgents(); }, tooltip: 'Refresh data', variant: 'icon' });
    b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    return b;
  }, [canAdd, load, loadAgents, handleExport]);

  const agentOptions = useMemo(() => agents.map((a) => ({ value: a.id, label: a.agentName })), [agents]);

  return (
    <div className="module-master-container" data-screen="print-printers">
      <ModuleHeader
        title="Printers"
        description="Kitchen and receipt printers, each reached through a Print Agent. Kitchen stations pick theirs on Kitchen Stations"
        icon={<IconSystem.print size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code, name, agent or address' }}
        filterConfig={{ onClick: () => { setDraftFilters(filters); setFilterOpen(!filterOpen); }, isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      {deleteError && (
        <div className="prn-banner" role="alert" data-delete-error>
          <span className="prn-banner__item">{deleteError}</span>
          <button type="button" className="prn-banner__link" onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid data={rows} columns={columns} enableSorting enablePagination manualPagination totalCount={total}
              currentPage={(query.page ?? 1) - 1} pageSize={query.pageSize ?? 25} pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={(i) => setQuery((q) => ({ ...q, page: i + 1 }))}
              onPageSizeChange={(s) => setQuery((q) => ({ ...q, pageSize: s, page: 1 }))}
              frozenRightColumns={['actions']} isLoading={loading} rowActions={getRowActions}
              emptyMessage={loadError ?? 'No printers yet. Pair a Print Agent first, then add its printers with +.'} />
          </div>
        </div>
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Agent" options={agentOptions} value={draftFilters.agentId ?? ''} placeholder="All agents" size="sm" isClearable
                menuPortalTarget={document.body} onChange={(v) => setDraftFilters((p) => ({ ...p, agentId: (v as string) || undefined }))} />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={[{ value: 'true', label: 'Include inactive' }]} value={draftFilters.includeInactive ?? ''}
                placeholder="Active only" size="sm" isClearable menuPortalTarget={document.body}
                onChange={(v) => setDraftFilters((p) => ({ ...p, includeInactive: (v as string) || undefined }))} />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setDraftFilters({}); setFilters({}); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setFilters(draftFilters); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      </div>

      <PrinterFormModal isOpen={!!form} mode={form?.mode ?? 'create'} record={form?.record} agents={agents}
        onClose={() => setForm(null)} onSaved={handleSaved} />
      <PrintTestPageModal printer={testing?.printer ?? null} queued={testing?.queued ?? null} onClose={() => { setTesting(null); void load(); }} />
      <ConfirmDialog open={!!deleting} title="Delete printer?"
        message={`Delete "${deleting?.printerName ?? ''}"? A printer a kitchen station still uses cannot be deleted; you will be told which.`}
        confirmLabel="Delete" cancelLabel="Keep it" variant="danger" loading={isDeleting} loadingMessage="Deleting..."
        onConfirm={() => void handleDelete()} onCancel={() => setDeleting(null)} />
    </div>
  );
};

export default PrintersScreen;
